import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ia, type Consumo, type Projecao } from '../api/ia'
import { Aviso } from '../componentes/Aviso'
import { Botao } from '../componentes/Botao'
import { Campo } from '../componentes/Campo'
import { Cartao } from '../componentes/Cartao'
import { Estado } from '../componentes/Estado'
import { formatar } from '../i18n'
import './usuarios.css'

/**
 * Consumo de IA e projeção de crédito.
 *
 * A tela existe por causa de 20/07: naquele dia o crédito da OpenAI acabou, o
 * erro chegou genérico e o atendimento parou sem que ninguém soubesse por quê.
 * O número que responde à pergunta certa não é o saldo — é **quantos dias
 * faltam no ritmo atual**, e é ele que aparece primeiro.
 */
export function ConsumoDeIa() {
  const { t } = useTranslation()

  const [consumo, setConsumo] = useState<Consumo | null>(null)
  const [projecao, setProjecao] = useState<Projecao | null>(null)
  const [falhou, setFalhou] = useState(false)
  const [dias, setDias] = useState(30)

  useEffect(() => {
    let vivo = true
    setFalhou(false)

    Promise.all([ia.consumo(dias), ia.saldo()])
      .then(([c, p]) => {
        if (!vivo) return
        setConsumo(c)
        setProjecao(p)
      })
      .catch(() => vivo && setFalhou(true))

    return () => {
      vivo = false
    }
  }, [dias])

  if (falhou)
    return (
      <Estado
        tipo="erro"
        acao={<Botao onClick={() => setDias((d) => d)}>{t('comum.tentarDeNovo')}</Botao>}
      />
    )

  if (!consumo || !projecao) return <Estado tipo="carregando" />

  return (
    <main className="usuarios">
      <header className="usuarios__cabecalho">
        <div>
          <h1 className="usuarios__titulo">{t('ia.titulo')}</h1>
          <p className="usuarios__descricao">{t('ia.descricao')}</p>
        </div>

        <label className="usuarios__opcao">
          <span>
            <strong>{t('ia.periodo')}</strong>
          </span>
          <select
            value={dias}
            onChange={(e) => setDias(Number(e.target.value))}
            aria-label={t('ia.periodo')}
          >
            <option value={7}>{t('ia.dias', { total: 7 })}</option>
            <option value={30}>{t('ia.dias', { total: 30 })}</option>
            <option value={90}>{t('ia.dias', { total: 90 })}</option>
          </select>
        </label>
      </header>

      {/* O alerta vem antes de qualquer número: se o crédito está acabando,
          é isso que precisa ser lido primeiro. */}
      {projecao.emAlerta && (
        <Aviso tom="perigo" titulo={t('ia.alertaTitulo')}>
          {t('ia.alertaCorpo', { dias: projecao.diasRestantes ?? '?' })}
        </Aviso>
      )}

      <Cartao titulo={t('ia.projecaoTitulo')}>
        <dl className="indicadores">
          <div className="indicador">
            <dt>{t('ia.diasRestantes')}</dt>
            <dd className={projecao.emAlerta ? 'indicador__valor indicador__valor--alerta' : 'indicador__valor'}>
              {projecao.diasRestantes === null ? '—' : formatar.numero(projecao.diasRestantes)}
            </dd>
            {/* Sem saldo informado não há projeção. Dizer isso é melhor que
                mostrar um traço que parece "está tudo bem". */}
            <dd className="indicador__nota">
              {projecao.saldoInformado === null ? t('ia.semSaldoInformado') : t('ia.noRitmoAtual')}
            </dd>
          </div>

          <div className="indicador">
            <dt>{t('ia.mediaDiaria')}</dt>
            <dd className="indicador__valor">{formatar.moeda(projecao.mediaDiaria)}</dd>
            <dd className="indicador__nota">
              {t('ia.mediaDe', { total: projecao.diasDoPeriodo })}
            </dd>
          </div>

          <div className="indicador">
            <dt>{t('ia.totalDoPeriodo')}</dt>
            <dd className="indicador__valor">{formatar.moeda(consumo.total)}</dd>
            <dd className="indicador__nota">
              {t('ia.chamadas', { total: consumo.chamadas })}
            </dd>
          </div>

          <div className="indicador">
            <dt>{t('ia.quedas')}</dt>
            <dd className="indicador__valor">{formatar.numero(consumo.reservas)}</dd>
            {/* Uso da reserva não é erro, mas é sinal — merece estar no painel
                e não escondido no log. */}
            <dd className="indicador__nota">{t('ia.quedasAjuda')}</dd>
          </div>
        </dl>
      </Cartao>

      <SaldoDaConta projecao={projecao} aoInformar={setProjecao} />

      <Cartao titulo={t('ia.porAssistente')}>
        {consumo.porAssistente.length === 0 ? (
          <Estado tipo="vazio" descricao={t('ia.semConsumo')} />
        ) : (
          <div
              className="usuarios__tabela-rolagem"
              // A região rolável precisa ser alcançável pelo teclado (WCAG 2.1.1):
              // sem isto, a parte escondida da tabela não existe para quem não usa mouse.
              tabIndex={0}
              role="group"
              aria-label={t('ia.legenda')}
            >
              <table className="usuarios__tabela">
              <caption className="usuarios__legenda">{t('ia.legenda')}</caption>
              <thead>
                <tr>
                  <th scope="col">{t('ia.assistente')}</th>
                  <th scope="col">{t('ia.chamadasColuna')}</th>
                  <th scope="col">{t('ia.tokens')}</th>
                  <th scope="col">{t('ia.custo')}</th>
                  <th scope="col">{t('ia.porConversa')}</th>
                </tr>
              </thead>
              <tbody>
                {consumo.porAssistente.map((a) => (
                  <tr key={a.assistenteId}>
                    <td>{a.nome}</td>
                    <td>{formatar.numero(a.chamadas)}</td>
                    <td>{formatar.numero(a.tokens)}</td>
                    <td>{formatar.moeda(a.custo)}</td>
                    <td>{formatar.moeda(a.custoMedioPorConversa)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Cartao>
    </main>
  )
}

/**
 * O saldo da conta do provedor, informado **pela tela** (demanda 140).
 *
 * 🔴 **Antes disto o caminho era o `.env` da VPS**, o que na prática significava
 * passar por nós toda semana — e `docker restart` nem relê o arquivo. Um valor
 * que precisa ser atualizado a cada recarga não pode morar onde só quem tem
 * chave da máquina alcança.
 *
 * ⭐ **E os quatro números aparecem juntos de propósito:** informado, quando,
 * consumido desde então e o que resta. É o que torna o número **conferível** —
 * quem lê "restam R$ 62" pode reconstruir a conta em vez de acreditar nela.
 */
function SaldoDaConta({
  projecao,
  aoInformar,
}: {
  projecao: Projecao
  aoInformar: (p: Projecao) => void
}) {
  const { t } = useTranslation()
  const [valor, setValor] = useState('')
  const [salvando, setSalvando] = useState(false)
  const [erro, setErro] = useState<string | null>(null)

  async function informar(e: React.FormEvent) {
    e.preventDefault()

    // ⚠️ Aceita vírgula e ponto: quem digita em português escreve "150,50", e
    // recusar isso seria transformar hábito de teclado em erro. O que NÃO pode
    // acontecer é virar zero em silêncio — que é o modo de falha do caminho
    // pelo `.env`.
    const numero = Number(valor.replace(/\./g, '').replace(',', '.'))

    if (!Number.isFinite(numero) || numero < 0) {
      setErro(t('ia.saldoInvalido'))
      return
    }

    setSalvando(true)
    setErro(null)

    try {
      aoInformar(await ia.informarSaldo(numero))
      setValor('')
    } catch {
      setErro(t('ia.saldoFalhou'))
    } finally {
      setSalvando(false)
    }
  }

  return (
    <Cartao titulo={t('ia.saldoTitulo')}>
      <dl className="indicadores">
        <div className="indicador">
          <dt>{t('ia.saldoAtual')}</dt>
          <dd className="indicador__valor">
            {projecao.saldoAtual === null ? '—' : formatar.moeda(projecao.saldoAtual)}
          </dd>
          <dd className="indicador__nota">
            {projecao.informadoEm === null
              ? t('ia.saldoNuncaInformado')
              : t('ia.saldoInformadoEm', {
                  data: formatar.dataHora(projecao.informadoEm),
                  valor: formatar.moeda(projecao.saldoInformado ?? 0),
                })}
          </dd>
        </div>

        {projecao.consumidoDesde !== null && (
          <div className="indicador">
            <dt>{t('ia.consumidoDesde')}</dt>
            <dd className="indicador__valor">{formatar.moeda(projecao.consumidoDesde)}</dd>
            <dd className="indicador__nota">{t('ia.consumidoDesdeAjuda')}</dd>
          </div>
        )}
      </dl>

      <form className="usuarios__formulario" onSubmit={informar}>
        <Campo
          rotulo={t('ia.saldoRotulo')}
          value={valor}
          onChange={(e) => setValor(e.target.value)}
          disabled={salvando}
          erro={erro ?? undefined}
          ajuda={t('ia.saldoAjuda')}
          inputMode="decimal"
        />

        <Botao type="submit" disabled={salvando || valor.trim().length === 0}>
          {salvando ? t('comum.salvando') : t('ia.saldoGravar')}
        </Botao>
      </form>
    </Cartao>
  )
}
